/**
 * v1/v2/v3 save-file importer (§3.6: the migration path is preserved).
 * Applies the prototype's own normalization rules, then lifts everything
 * into schema-v4 records. Every imported quantity is provenance-tagged.
 */
import { newId, nowIso } from '../core/ids';
import { toCents } from '../core/money';
import { lineLengthFt, polygonAreaSqFt, type LatLng } from '../core/geo';
import { defaultSettings, type Bundle } from '../core/db';
import { SCHEMA_VERSION } from '../core/types';
import type {
  Condition, DocumentRecord, Envelope, InventoryCat, InventoryItem, Job, JobStatus,
  PriceItem, PriceKind, Settings, Shape, TakeoffLine,
} from '../core/types';

const OWNER = 'david';
const CATS: InventoryCat[] = ['tree', 'palm', 'shrub', 'cactus', 'hedge', 'bed', 'turf', 'valve', 'drain', 'other'];
const CONDS: Condition[] = ['good', 'fair', 'poor', 'dead', 'unknown'];

/* eslint-disable @typescript-eslint/no-explicit-any */
type Legacy = any;

export function isLegacyFile(obj: unknown): boolean {
  const o = obj as Legacy;
  return !!o && o.app === 'landscaping-bid-studio' && Array.isArray(o.jobs);
}

function env(): Envelope {
  const now = nowIso();
  return { id: newId(), owner: OWNER, createdAt: now, updatedAt: now, rev: 1, deletedAt: null };
}

const num = (v: unknown): number => {
  const n = typeof v === 'string' ? parseFloat(v) : (v as number);
  return Number.isFinite(n) ? n : 0;
};
const str = (v: unknown): string => (v == null ? '' : String(v));

function mapStatus(s: unknown): JobStatus {
  const m: Record<string, JobStatus> = {
    lead: 'lead', sent: 'bid_sent', bid_sent: 'bid_sent', won: 'won', lost: 'lost',
    progress: 'in_progress', in_progress: 'in_progress', complete: 'complete', done: 'complete',
  };
  return m[str(s)] ?? 'lead';
}

function legacyCoords(v: unknown): LatLng[] | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  const pts = v.filter((p: Legacy) => Array.isArray(p) && p.length >= 2
    && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  return pts.length ? (pts as LatLng[]) : null;
}

/** Convert a validated legacy save file into a v4 restore bundle. */
export function convertLegacy(file: Legacy): Bundle {
  // --- prototype's own v1/v2 normalization, preserved verbatim in effect ---
  for (const j of file.jobs) {
    if (!Array.isArray(j.inventory)) j.inventory = [];
    if (!j.survey) j.survey = 'site';
    if (j.parcel === undefined) j.parcel = null;
    if (!Array.isArray(j.features)) j.features = [];
    if (!Array.isArray(j.extras)) j.extras = [];
    if (!Array.isArray(j.updates)) j.updates = [];
    if (!j.rateOv) j.rateOv = {};
    for (const f of j.features) {
      if (f.verified === undefined) f.verified = false;
      if (f.note === undefined) f.note = '';
    }
  }

  // --- settings ---
  const s = file.settings ?? {};
  const base = defaultSettings();
  const settings: Settings = {
    ...base,
    company: {
      name: str(s.company) || base.company.name,
      owner: str(s.owner) || base.company.owner,
      phone: str(s.phone), email: str(s.email),
      roc: str(s.license) || base.company.roc,
    },
    defaults: {
      markupPct: num(s.markup), taxPct: num(s.tax),
      depositPct: s.deposit != null ? num(s.deposit) : base.defaults.depositPct,
      validityDays: s.valid != null ? num(s.valid) : base.defaults.validityDays,
    },
    basemap: ['usgs', 'esri', 'osm'].includes(s.basemap) ? s.basemap : 'usgs',
    termsText: str(s.terms) || base.termsText,
    bidSeq: s.seq != null ? num(s.seq) : file.seq != null ? num(file.seq) : base.bidSeq,
  };

  // --- price book ---
  const priceBook: PriceItem[] = (Array.isArray(file.priceBook) ? file.priceBook : []).map((p: Legacy): PriceItem => ({
    ...env(),
    itemId: str(p.id), name: str(p.name), unit: str(p.unit),
    kind: (['area', 'line', 'count', 'service'].includes(p.kind) ? p.kind : 'count') as PriceKind,
    rateCents: toCents(num(p.rate)), retired: false,
  }));
  const priceKind = new Map(priceBook.map((p) => [p.itemId, p.kind]));

  // --- jobs and children ---
  const jobs: Job[] = [];
  const shapes: Shape[] = [];
  const takeoffLines: TakeoffLine[] = [];
  const inventoryItems: InventoryItem[] = [];
  const documents: DocumentRecord[] = [];
  const importedAt = nowIso();

  for (const lj of file.jobs) {
    const job: Job = {
      ...env(),
      clientId: undefined,
      title: str(lj.title) || str(lj.docNum) || 'Imported job',
      siteAddress: str(lj.client?.address),
      lat: Number.isFinite(lj.site?.lat) ? lj.site.lat : undefined,
      lng: Number.isFinite(lj.site?.lng) ? lj.site.lng : undefined,
      zoom: Number.isFinite(lj.site?.zoom) ? lj.site.zoom : undefined,
      status: mapStatus(lj.status),
      bidNumber: str(lj.docNum) || 'B-0000',
      notes: str(lj.client?.notes),
      fin: {
        markupPct: num(lj.fin?.markup), taxPct: num(lj.fin?.tax),
        depositPct: lj.fin?.deposit != null ? num(lj.fin.deposit) : settings.defaults.depositPct,
        adjustCents: toCents(num(lj.fin?.adjust)),
      },
      parcel: lj.parcel && lj.parcel.apn ? {
        provider: 'maricopa', apn: str(lj.parcel.apn), address: str(lj.parcel.address),
        rings: Array.isArray(lj.parcel.rings) ? lj.parcel.rings : [],
        lotSqFt: num(lj.parcel.lotSqFt), perimeterFt: num(lj.parcel.perimeterFt),
        fetchedAt: importedAt,
      } : null,
    };
    jobs.push(job);

    const clientName = str(lj.client?.name);
    if (clientName) {
      // client contact rides on the job notes import; full client records are created on first edit
      job.notes = [job.notes, `Client: ${clientName} ${str(lj.client?.phone)} ${str(lj.client?.email)}`.trim()]
        .filter(Boolean).join('\n');
    }

    const rateOv: Record<string, unknown> = lj.rateOv ?? {};

    for (const f of lj.features) {
      const line: TakeoffLine = {
        ...env(),
        jobId: job.id,
        priceItemId: str(f.priceId) || undefined,
        qty: num(f.qty),
        label: str(f.label),
        unitPriceCentsOverride: rateOv[f.priceId] != null ? toCents(num(rateOv[f.priceId])) : undefined,
        provenance: {
          source: f.src === 'scan' ? 'scan' : 'import',
          setBy: OWNER, setAt: importedAt, detail: 'legacy-file',
        },
        verification: { verified: !!f.verified, note: str(f.note) || undefined },
      };
      const coords = legacyCoords(f.coords);
      if (coords) {
        const kind = priceKind.get(str(f.priceId)) ?? 'count';
        const shape: Shape = {
          ...env(), jobId: job.id,
          kind: kind === 'area' ? 'polygon' : kind === 'line' ? 'line' : 'points',
          coords,
          takeoffLineId: line.id,
        };
        if (kind === 'area') {
          shape.measure = { value: polygonAreaSqFt(coords), unit: 'sqft', method: 'karney', computedAt: importedAt };
        } else if (kind === 'line') {
          shape.measure = { value: lineLengthFt(coords), unit: 'linft', method: 'karney', computedAt: importedAt };
        } else {
          shape.measure = { value: coords.length, unit: 'count', method: 'manual', computedAt: importedAt };
        }
        line.shapeId = shape.id;
        shapes.push(shape);
      }
      takeoffLines.push(line);
    }

    for (const x of lj.extras) {
      takeoffLines.push({
        ...env(), jobId: job.id,
        custom: { name: str(x.name), unit: str(x.unit) || 'each' },
        qty: num(x.qty), label: str(x.name),
        unitPriceCentsOverride: toCents(num(x.rate)),
        provenance: { source: 'import', setBy: OWNER, setAt: importedAt, detail: 'legacy-extra' },
        verification: { verified: false },
      });
    }

    for (const it of lj.inventory) {
      inventoryItems.push({
        ...env(), jobId: job.id,
        cat: CATS.includes(it.cat) ? it.cat : 'other',
        species: str(it.species) || undefined,
        qty: num(it.qty) || 1,
        cond: CONDS.includes(it.cond) ? it.cond : 'unknown',
        notes: str(it.notes) || undefined,
        photoIds: [],
        osmId: typeof it.id === 'string' && it.id.startsWith('osm') ? it.id : undefined,
        provenance: {
          source: it.src === 'scan' ? 'scan' : 'import',
          setBy: OWNER, setAt: importedAt, detail: 'legacy-file',
        },
        verification: { verified: !!it.verified },
      });
    }

    let updSeq = 0;
    for (const u of lj.updates) {
      updSeq += 1;
      documents.push({
        ...env(), jobId: job.id, type: 'progress_update',
        number: `${job.bidNumber}-U${updSeq}`, status: 'draft',
        payload: {
          company: settings.company, clientName, siteAddress: job.siteAddress,
          bidNumber: job.bidNumber, issuedDate: str(u.date), validityDays: 0,
          lines: [], math: {
            subtotalCents: 0, markupPct: 0, markupCents: 0, adjustCents: 0,
            taxPct: 0, taxCents: 0, totalCents: 0, depositPct: 0, depositCents: 0,
          },
          termsText: '',
          body: {
            summary: str(u.summary), next: str(u.next), notes: str(u.notes),
            eta: str(u.eta), schedule: str(u.schedule),
            lineStatus: JSON.stringify(u.lineStatus ?? {}),
          },
        },
      });
    }
  }

  return {
    app: 'forever-green-bid-studio', schema: SCHEMA_VERSION, exportedAt: importedAt,
    settings,
    data: {
      clients: [], jobs, shapes, takeoffLines, inventoryItems, priceBook,
      documents, photos: [], reviewQueue: [], aiCalls: [], outbox: [],
    },
  };
}
